import {
  Button,
  Group,
  Image,
  Modal,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconPhoto, IconPlayerPause, IconPlayerPlay } from '@tabler/icons-react'
import { createFileRoute } from '@tanstack/react-router'
import { useAuth } from '~/Auth'
import { useSetArtistCoverArt } from '~/api/mutations'
import {
  useArtist,
  useArtistAlbums,
  useArtistTracks,
  useSimilarArtists,
  useUser,
} from '~/api/queries'
import { AlbumGrid } from '~/components/blocks/AlbumGrid/AlbumGrid'
import { ArtistGrid } from '~/components/blocks/ArtistGrid/ArtistGrid'
import { CoverArt } from '~/components/blocks/CoverArt/CoverArt'
import { EnqueueButton } from '~/components/blocks/EnqueueButton'
import { FavoriteButton } from '~/components/blocks/FavoriteButton/FavoriteButton'
import { trackToQueueItem } from '~/components/player/utils'
import { usePlayer } from '~/hooks/use-player'
import { artistImageUrl, coverArtUrl } from '~/lib/cover-art'

export const Route = createFileRoute('/_app/artists/$artistId')({
  component: ArtistDetailPage,
})

function ArtistDetailPage() {
  const { artistId: artistIdParam } = Route.useParams()
  const artistId = Number(artistIdParam)
  const { session } = useAuth()
  const currentUserId = (session.data?.user as { id?: number } | undefined)?.id ?? 0
  const { data: currentUser } = useUser(currentUserId)
  const showNonLibrary = currentUser?.config.ui.showNonLibraryResults ?? false

  const { data: artist, isLoading } = useArtist(artistId)
  const { data: albums } = useArtistAlbums(artistId, { limit: 100 })
  const { data: tracksData } = useArtistTracks(artistId, { limit: 500 })
  const { data: similarArtistsData } = useSimilarArtists(artistId, showNonLibrary)
  const player = usePlayer()
  const [coverModalOpen, { open: openCoverModal, close: closeCoverModal }] = useDisclosure(false)
  const setCoverArt = useSetArtistCoverArt()

  if (isLoading) return <Skeleton h={400} />
  if (!artist) return <Text>Artist not found</Text>

  const tracks = tracksData?.data ?? []
  const currentTrack = player.state.queue[player.state.currentIndex]
  const isThisArtistContext = !player.state.radioMode && currentTrack?.artistId === artistId
  const isPlayingThisArtist = isThisArtistContext && player.state.isPlaying
  const imageUrl = artistImageUrl(artist)

  const handlePlayAll = () => {
    if (isPlayingThisArtist) {
      player.pause()
    } else if (isThisArtistContext) {
      player.resume()
    } else if (tracks.length > 0) {
      player.play(tracks.map(trackToQueueItem))
    }
  }

  return (
    <Stack gap="lg">
      <Group align="flex-start" gap="xl">
        <Stack gap="xs" style={{ width: 200, flexShrink: 0 }}>
          <CoverArt
            key={artist.coverArt ?? 'no-cover'}
            type="artists"
            id={artist.id}
            alt={artist.name}
            size={300}
            fallbackUrl={imageUrl}
            radius="md"
            w={200}
            h={200}
          />
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconPhoto size={14} />}
            onClick={openCoverModal}
          >
            Change cover
          </Button>
        </Stack>
        <Stack gap="xs" style={{ flex: 1 }}>
          <Title order={1}>{artist.name}</Title>
          <Text c="dimmed">
            {artist.albumCount} album{artist.albumCount !== 1 ? 's' : ''}
          </Text>
          <Group>
            {tracks.length > 0 && (
              <Button
                leftSection={
                  isPlayingThisArtist ? <IconPlayerPause size={16} /> : <IconPlayerPlay size={16} />
                }
                onClick={handlePlayAll}
              >
                {isPlayingThisArtist ? 'Pause' : 'Play All'}
              </Button>
            )}
            {tracks.length > 0 && <EnqueueButton tracks={tracks.map(trackToQueueItem)} />}
            <FavoriteButton type="artist" id={artist.id} favorited={artist.favorited} />
            {artist.lastFmUrl && (
              <Button
                component="a"
                href={artist.lastFmUrl}
                target="_blank"
                variant="subtle"
                size="xs"
              >
                Last.fm
              </Button>
            )}
          </Group>
        </Stack>
      </Group>

      {artist.biography && <Text style={{ whiteSpace: 'pre-line' }}>{artist.biography}</Text>}

      {albums?.data && albums.data.length > 0 && (
        <section>
          <Title order={3} mb="md">
            Albums
          </Title>
          <AlbumGrid albums={albums.data} />
        </section>
      )}

      {similarArtistsData?.data && similarArtistsData.data.length > 0 && (
        <section>
          <Title order={3} mb="md">
            Similar Artists
          </Title>
          <ArtistGrid artists={similarArtistsData.data} />
        </section>
      )}

      <Modal opened={coverModalOpen} onClose={closeCoverModal} title="Set artist cover" size="lg">
        {albums?.data && albums.data.length > 0 ? (
          <SimpleGrid cols={4} spacing="xs">
            {albums.data.map((album) => (
              <Tooltip key={album.id} label={album.name} withArrow>
                <UnstyledButton
                  onClick={() => {
                    setCoverArt.mutate({ artistId, albumId: album.id })
                    closeCoverModal()
                  }}
                >
                  <Image
                    src={coverArtUrl('albums', album.id, 150)}
                    alt={album.name}
                    radius="sm"
                    style={{ aspectRatio: '1', objectFit: 'cover' }}
                  />
                </UnstyledButton>
              </Tooltip>
            ))}
          </SimpleGrid>
        ) : (
          <Text c="dimmed">No albums found.</Text>
        )}
      </Modal>
    </Stack>
  )
}
